import React, { useState } from "react";
import { Download } from "lucide-react";
import { usePackagesStore } from "../../../Packages/store/packagesStore";
import { usePaymentsStore } from "../../store/paymentsStore";
import { BillingPayment } from "../../types/payment";
import * as XLSX from 'xlsx';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const tabs = ["All", "By Package", "By Service", "By Date Range"];

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose}) => {
  const [activeTab, setActiveTab] = useState("All");
  const [selectedPackage, setSelectedPackage] = useState("All");
  const [selectedService, setSelectedService] = useState("All");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const { packages } = usePackagesStore();
  const { payments } = usePaymentsStore();

  const handleExport = (e: React.FormEvent) => {
    e.preventDefault();

    let filtered: BillingPayment[] = payments;

    if (activeTab === "By Package" && selectedPackage !== "All") {
      filtered = filtered.filter(p => p.product === selectedPackage);
    }

    if (activeTab === "By Service" && selectedService !== "All") {
      filtered = filtered.filter(p => p.service_name === selectedService);
    }

    if (activeTab === "By Date Range" && dateRange.from && dateRange.to) {
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);

      filtered = filtered.filter(p => {
        const d = new Date(p.created_at);
        return d >= fromDate && d <= toDate;
      });
    }

    exportToExcel(filtered);
    onClose();
  };

  const exportToExcel = (payments: BillingPayment[]) => {
    const wb = XLSX.utils.book_new();

    const ws = XLSX.utils.json_to_sheet(
      payments.map(p => ({
        Date: p.created_at,
        "User Name": p.username,
        "User ID": p.userid,
        Amount: p.amount,
        Currency: p.currency,
        Gateway: p.payment_gateway,
        Status: p.status,
        Service: p.service_name,
        Package: p.product,
        "Payer ID": p.payerid || "N/A",
      }))
    );

    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, `payments_${new Date().toISOString().split("T")[0]}.xlsx`);
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Export Data</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="flex">
          {/* Sidebar Tabs */}
          <div className="w-1/4 border-r p-4 space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`block w-full text-left px-3 py-2 rounded-md ${activeTab === tab
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="w-3/4 p-6">
            <form onSubmit={handleExport} className="space-y-4">
              {activeTab === "All" && (
                <div>
                  <p className="text-gray-700">
                    Export all Payments without filters.
                  </p>
                </div>
              )}

              {activeTab === "By Package" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Package
                  </label>
                  <select
                    className="w-full p-2 border rounded"
                    value={selectedPackage}
                    onChange={(e) => setSelectedPackage(e.target.value)}
                  >
                    <option value="All">All Packages</option>
                    {packages.map((pkg, index) => (
                      <option key={index} value={pkg.name}>
                        {pkg.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {activeTab === "By Service" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Service
                  </label>
                  <select
                    className="w-full p-2 border rounded"
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                  >
                    <option value="All">All Services</option>
                    <option value="kanema">Kanema Online</option>
                    <option value="mingoli">Mingoli</option>
                    <option value="katswiri">Katswiri</option>
                    <option value="nkhani">Nkhani</option>
                  </select>
                </div>
              )}

              {activeTab === "By Date Range" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      className="w-full p-2 border rounded"
                      value={dateRange.from}
                      onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      className="w-full p-2 border rounded"
                      value={dateRange.to}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2 hover:bg-blue-700"
                >
                  <Download size={16} />
                  Export
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
